import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough, Heading1, Heading2, Heading3,
  List as ListIcon, ListOrdered, ListChecks, Quote, Code, Link as LinkIcon, Undo2, Redo2,
  Minus,
} from "lucide-react";
import { useCallback, useEffect } from "react";

function ToolbarBtn({ onClick, active, disabled, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testid}
      className={`p-1.5 rounded text-sm transition border ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-transparent hover:bg-slate-100"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="w-px h-5 bg-slate-200 mx-1 self-center" />;
}

/**
 * Rich-text editor with bold/italic/underline/strike, 3 heading levels,
 * bullet/ordered/task lists, blockquote, code block, link, undo/redo.
 *
 * value/onChange use raw HTML (Tiptap output) — server-side sanitiser strips
 * anything outside the allowed tag set before persisting.
 */
export default function RichTextEditor({ value, onChange, placeholder = "Start writing…", minHeight = 220, disabled = false, testid = "rich-editor" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: "text-cyan-700 underline" } }),
      Placeholder.configure({ placeholder }),
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: value || "",
    editable: !disabled,
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tiptap-content prose prose-sm max-w-none focus:outline-none px-3 py-3",
        "data-testid": `${testid}-content`,
      },
    },
  });

  // Sync external value (e.g. report reload) without breaking focus.
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL (leave empty to remove)", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  if (!editor) return <div className="border border-slate-200 rounded-md p-3 text-sm text-slate-400">Loading editor…</div>;

  const is = (name, opts) => editor.isActive(name, opts);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={is("bold")} title="Bold" testid={`${testid}-bold`}>
          <Bold className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={is("italic")} title="Italic" testid={`${testid}-italic`}>
          <Italic className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={is("underline")} title="Underline" testid={`${testid}-underline`}>
          <UnderlineIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={is("strike")} title="Strikethrough" testid={`${testid}-strike`}>
          <Strikethrough className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={is("heading", { level: 1 })} title="H1" testid={`${testid}-h1`}>
          <Heading1 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={is("heading", { level: 2 })} title="H2" testid={`${testid}-h2`}>
          <Heading2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={is("heading", { level: 3 })} title="H3" testid={`${testid}-h3`}>
          <Heading3 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={is("bulletList")} title="Bullet list" testid={`${testid}-ul`}>
          <ListIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={is("orderedList")} title="Ordered list" testid={`${testid}-ol`}>
          <ListOrdered className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleTaskList().run()} active={is("taskList")} title="Task list" testid={`${testid}-tasks`}>
          <ListChecks className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={is("blockquote")} title="Quote" testid={`${testid}-quote`}>
          <Quote className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={is("codeBlock")} title="Code block" testid={`${testid}-code`}>
          <Code className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={setLink} active={is("link")} title="Link" testid={`${testid}-link`}>
          <LinkIcon className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Divider" testid={`${testid}-hr`}>
          <Minus className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <Divider />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo" testid={`${testid}-undo`}>
          <Undo2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo" testid={`${testid}-redo`}>
          <Redo2 className="w-3.5 h-3.5" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} style={{ minHeight }} />
    </div>
  );
}
